import { Grade } from "./types";


export const checkH1InContent = (content: string) => {
  const regex = /<h1[^>]*>(.*?)<\/h1>/i; // Regex to check for H1 tags
  return regex.test(content);
};

export const getPageTitle = (content: string): string => {
  const h1Regex = /<h1\b[^>]*>(.*?)<\/h1>/i;
  const match = content.match(h1Regex);
  return match ? match[1].replace(/<[^>]*>/g, '') : 'Sample Page Title';
};

export const checkKeywordPresenceInContent = (content: string, keyword: string): boolean => {
  const regex = new RegExp(`\\b${keyword}\\b`, 'i');
  return regex.test(content);
};

export const countWordsInContent = (content: string): number => {
  return content.split(/\s+/).filter((word) => word.trim().length > 0).length;
};

export const countImagesInContent = (content: string): number => {
  return (content.match(/<img[^>]*>/g) || []).length;
};

export const countLinksInContent = (content: string): number => {
  return (content.match(/<a[^>]*>/g) || []).length;
};

// new functions
export const checkKeywordInTitle = (title: string, keyword: string): boolean => {
  return title.toLowerCase().includes(keyword.toLowerCase());
};//done

export const checkKeywordAtBeginning = (htmlContent: string, keyword: string): boolean => {
  // Extract text from the first H1 tag
  const h1Match = htmlContent.match(/<h1[^>]*>(.*?)<\/h1>/i);
  const title = h1Match ? h1Match[1].replace(/<[^>]+>/g, '').trim() : '';

  // Clean the title and keyword
  const cleanTitle = title.toLowerCase().replace(/[^\w\s]|_/g, '').replace(/\s+/g, ' ');
  const cleanKeyword = keyword.toLowerCase().replace(/[^\w\s]|_/g, '').replace(/\s+/g, ' ');

  // Check if title starts with keyword, allowing for optional colon or space after
  return new RegExp(`^${cleanKeyword}\\b([: ]|$)`, 'i').test(cleanTitle);
};// done



export const checkPageTitleLength = (content: string): { length: number; grade: Grade } => {
  const h1Match = content.match(/<h1[^>]*>(.*?)<\/h1>/i);
  const title = h1Match?.[1]?.replace(/<[^>]+>/g, '').trim() || '';

  const length = title.length;

  if (length >= 30 && length <= 60) return { length, grade: "Excellent" };
  if (length === 0) return { length, grade: "Poor" };
  return { length, grade: "Poor" };
};// done


export const checkMetaDescriptionExists = (content: string): boolean => {
  // More robust regex that handles different HTML formats
  const metaRegex = /<meta\s+[^>]*name\s*=\s*["']description["'][^>]*content\s*=\s*["']([^"']*)["'][^>]*>/i;
  return metaRegex.test(content);
};// not done

export const getMetaDescriptionContent = (content: string): string | null => {
  const metaRegex = /<meta\s+[^>]*name\s*=\s*["']description["'][^>]*content\s*=\s*["']([^"']*)["'][^>]*>/i;
  const match = content.match(metaRegex);
  return match ? match[1] : null;
}; // not done

export const checkKeywordInH1 = (content: string, keyword: string): boolean => {
  if (!keyword) return false; // Return false if no keyword

  const h1Match = content.match(/<h1[^>]*>(.*?)<\/h1>/i);
  if (!h1Match) return false;

  const h1Text = h1Match[1].replace(/<[^>]+>/g, '').trim();
  return h1Text.toLowerCase().includes(keyword.toLowerCase());
};// done


export const checkKeywordInFirstParagraph = (content: string, keyword: string): boolean => {
  if (!keyword) return false;

  const firstParagraphMatch = content.match(/<p[^>]*>(.*?)<\/p>|(\n\n?)(.*?)(\n\n?)/i);
  if (!firstParagraphMatch) return false;

  const paragraphText = (firstParagraphMatch[1] || firstParagraphMatch[3] || '')
    .replace(/<[^>]+>/g, '')
    .trim();

  return paragraphText.toLowerCase().includes(keyword.toLowerCase());
}; //done  


export const calculateKeywordDensity = (
  content: string,
  keyword: string
): number => {
  if (!keyword) return 0;

  // Remove HTML tags and extra spaces
  const cleanContent = content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ');
  const totalWords = cleanContent.trim().split(/\s+/).length;

  // Count keyword matches (case-insensitive, whole words only)
  const keywordRegex = new RegExp(`\\b${keyword}\\b`, 'gi');
  const keywordCount = (cleanContent.match(keywordRegex) || []).length;

  return totalWords > 0
    ? Number(((keywordCount / totalWords) * 100).toFixed(1))
    : 0;
}; //done


export const checkAdditionalKeywordPresence = (
  content: string,
  keyword: string
): boolean => {
  if (!keyword) return false;

  // Clean content similar to density calculation
  const cleanContent = content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ');
  
  // Create regex pattern for whole word match
  const keywordRegex = new RegExp(`\\b${keyword}\\b`, 'gi');
  
  return keywordRegex.test(cleanContent);
};

export const calculateAdditionalKeywordDensity = (
  content: string,
  keyword: string
): number => {
  if (!keyword) return 0;

  // Remove HTML tags and extra spaces
  const cleanContent = content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ');
  const totalWords = cleanContent.trim().split(/\s+/).length;

  // Count keyword matches (case-insensitive, whole words only)
  const keywordRegex = new RegExp(`\\b${keyword}\\b`, 'gi');
  const keywordCount = (cleanContent.match(keywordRegex) || []).length;

  return totalWords > 0
    ? Number(((keywordCount / totalWords) * 100).toFixed(1))
    : 0;
}; //done


export const checkKeywordInImageName = (
  content: string,
  keyword: string
): boolean => {
  if (!keyword) return false;

  // Extract all image filenames from <img> tags
  const imgRegex = /<img[^>]+src="([^"]+)"[^>]*>/gi;
  const imageSources: string[] = [];
  let match;

  while ((match = imgRegex.exec(content)) !== null) {
    const src = match[1].split('/').pop() || ''; // Get filename from URL
    imageSources.push(src.split('?')[0]); // Remove URL parameters
  }

  // Check if any filename contains the keyword (case-insensitive)
  return imageSources.some(filename => {
    const nameWithoutExtension = filename.split('.')[0]; // Remove file extension
    return nameWithoutExtension.toLowerCase().includes(keyword.toLowerCase());
  });
}; // done



export const checkKeywordInImageAlt = (
  content: string,
  keyword: string
): boolean => {
  if (!keyword) return false;

  // Regex to find all alt text in <img> tags (handles single/double quotes)
  const altRegex = /<img[^>]*alt=(["'])(.*?)\1[^>]*>/gi;
  const altTexts: string[] = [];
  let match;

  while ((match = altRegex.exec(content)) !== null) {
    if (match[2]) { // Ignore empty alt tags
      altTexts.push(match[2].trim());
    }
  }

  // Check if any alt text contains the keyword (case-insensitive)
  return altTexts.some(alt =>
    alt.toLowerCase().includes(keyword.toLowerCase())
  );
};// not done

